import React, { KeyboardEventHandler, useContext, useMemo } from "react";
import { CarouselContext } from "./context";
import { cn } from "@/utils/tailwind";

interface Props<T> {
  items: readonly T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  transitionMs?: number;
}

export function Carousel<T>({
  items,
  renderItem,
  transitionMs = 350,
}: Props<T>) {
  const { index, visible, next, previous, maxIndex } =
    useContext(CarouselContext);

  const trackTransform = useMemo(
    () => `translateX(-${(index * 100) / visible}%)`,
    [index, visible],
  );

  const onKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      next();
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      previous();
    }
  };

  return (
    <div
      className="relative w-full overflow-x-hidden"
      onKeyDown={onKeyDown}
      tabIndex={0}
      role="group"
      aria-roledescription="carousel"
      aria-live="polite"
    >
      <div
        aria-hidden
        className={cn(
          "lg:w-2/12 w-1/12 pointer-events-none absolute inset-y-0 left-0 z-10  bg-linear-to-l from-white/0 to-white transition-opacity",
          index > 0 ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        aria-hidden
        className={cn(
          "lg:w-2/12 w-1/12 pointer-events-none absolute inset-y-0 right-0 z-10 bg-linear-to-r from-white/0 to-white transition-opacity",
          index < maxIndex ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        className="flex flex-row items-stretch h-full will-change-transform"
        style={{
          transform: trackTransform,
          transitionProperty: "transform",
          transitionTimingFunction: "ease",
          transitionDuration: `${transitionMs}ms`,
        }}
      >
        {items.map((item, i) => (
          <div
            key={i}
            className="box-border flex flex-col p-2"
            style={{ flex: `0 0 ${100 / visible}%` }}
          >
            <div className="flex-1">{renderItem(item, i)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
